import { useState } from 'react';
import { Play, Pause, Trash2, Edit, Zap, Clock, CheckCircle, AlertCircle } from 'lucide-react';

export interface AutomationFlow {
  id: string;
  name: string;
  description: string;
  trigger: {
    type: 'schedule' | 'event' | 'webhook' | 'manual';
    label: string;
  };
  actions: Array<{
    type: string;
    label: string;
  }>;
  status: 'active' | 'paused' | 'error';
  lastRun?: string;
  runCount: number;
}

interface AutomationCardProps {
  automation: AutomationFlow;
  onToggle: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export default function AutomationCard({ automation, onToggle, onEdit, onDelete }: AutomationCardProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = () => {
    if (isDeleting) {
      onDelete(automation.id);
    } else {
      setIsDeleting(true);
      setTimeout(() => setIsDeleting(false), 3000);
    }
  };

  const statusConfig = {
    active: {
      color: 'text-green-500',
      bg: 'bg-green-500/10',
      border: 'border-green-500/20',
      icon: CheckCircle,
      label: 'Active'
    },
    paused: {
      color: 'text-recipe-text-muted',
      bg: 'bg-recipe-border',
      border: 'border-recipe-border',
      icon: Pause,
      label: 'Paused'
    },
    error: {
      color: 'text-red-500',
      bg: 'bg-red-500/10',
      border: 'border-red-500/20',
      icon: AlertCircle,
      label: 'Error'
    }
  };

  const config = statusConfig[automation.status];
  const StatusIcon = config.icon;

  const triggerIcons = {
    schedule: Clock,
    event: Zap,
    webhook: Zap,
    manual: Play
  };

  const TriggerIcon = triggerIcons[automation.trigger.type];

  return (
    <div className="bg-recipe-card border border-recipe-border rounded-xl p-6 hover:border-recipe-accent/50 transition-all hover:shadow-lg group">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-recipe-text">{automation.name}</h3>
            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${config.bg} ${config.color} border ${config.border}`}>
              <StatusIcon className="w-3 h-3" />
              {config.label}
            </span>
          </div>
          <p className="text-sm text-recipe-text-muted">{automation.description}</p>
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => onToggle(automation.id)}
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300
            ${automation.status === 'active' ? 'bg-recipe-accent' : 'bg-recipe-border'}
          `}
        >
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300
              ${automation.status === 'active' ? 'translate-x-6' : 'translate-x-1'}
            `}
          />
        </button>
      </div>

      {/* Trigger & Actions Flow */}
      <div className="bg-recipe-bg border border-recipe-border rounded-lg p-4 mb-4">
        <div className="flex items-center gap-3">
          {/* Trigger */}
          <div className="flex items-center gap-2 px-3 py-2 bg-recipe-card border border-recipe-border rounded-lg flex-shrink-0">
            <TriggerIcon className="w-4 h-4 text-recipe-accent" />
            <span className="text-sm text-recipe-text font-medium">{automation.trigger.label}</span>
          </div>

          {/* Arrow */}
          <div className="flex-1 flex items-center">
            <div className="h-0.5 bg-recipe-border flex-1"></div>
            <Zap className="w-4 h-4 text-recipe-accent mx-2" />
            <div className="h-0.5 bg-recipe-border flex-1"></div>
          </div>

          {/* Actions Count */}
          <div className="flex items-center gap-2 px-3 py-2 bg-recipe-card border border-recipe-border rounded-lg flex-shrink-0">
            <span className="text-sm text-recipe-text font-medium">
              {automation.actions.length} {automation.actions.length === 1 ? 'Action' : 'Actions'}
            </span>
          </div>
        </div>

        {/* Actions List */}
        <div className="mt-3 flex flex-wrap gap-2">
          {automation.actions.map((action, index) => (
            <span
              key={index}
              className="text-xs text-recipe-text-muted bg-recipe-bg border border-recipe-border px-2 py-1 rounded"
            >
              {action.label}
            </span>
          ))}
        </div>
      </div>

      {/* Footer Stats & Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-recipe-border">
        <div className="flex items-center gap-6 text-xs text-recipe-text-muted">
          <div className="flex items-center gap-1">
            <Play className="w-3 h-3" />
            <span>{automation.runCount} runs</span>
          </div>
          {automation.lastRun && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>Last: {automation.lastRun}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(automation.id)}
            className="p-2 hover:bg-recipe-bg rounded-lg transition-colors text-recipe-text-muted hover:text-recipe-accent"
            title="Edit automation"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={handleDelete}
            className={`
              p-2 rounded-lg transition-colors
              ${isDeleting
                ? 'bg-red-500 text-white'
                : 'hover:bg-recipe-bg text-recipe-text-muted hover:text-red-500'
              }
            `}
            title={isDeleting ? 'Click again to confirm' : 'Delete automation'}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
